import React, { useCallback } from 'react'
import type { SearchFloatingPanelProps } from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import SearchFloatingPanel from 'components/SearchV2/Components/Mobile/Panels/SearchFloatingPanel'
import { useIntl } from 'react-intl'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import noop from 'lib/function/noop'
import useLocationSearchLabels from 'hooks/Search/useLocationSearchLabels'

interface Props extends
  Omit<
    SearchFloatingPanelProps,
    'title' | 'confirmText' | 'isConfirmButtonHidden' | 'children'
  >
{
  searchTypes?: Array<App.SearchType>
  typeaheadTypes?: Array<App.SearchPlaceType>
  placeholder?: string
  isSecondaryLocation?: boolean
  title?: string
  popularPlaces?: Array<App.SearchItem>
  typeaheadVertical?: App.SearchPlaceVertical
  differentLocations?: boolean
  recentSearches?: Array<App.FullRecentSearch>
  overrideSearchItems?: Array<App.SearchItem>
  onChange?: (item?: App.SearchItem) => void
  updateSearchSessionId?: (newSessionId: string) => void
  multiline?: boolean
  showNaturalLanguageSearchSuggestions?: boolean
  label?: string
  userInteractions?: {
    dates: boolean
    travelers: boolean
    location: boolean
  }
  setTravelDatesShowAIGlow?: (show: boolean) => void
  setOccupancyShowAIGlow?: (show: boolean) => void
  /** Temporary: Ref to expose typeaheadId and searchPhrase for search submission */
  temporarySearchSubmitRef?: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
  onUserSelectedLocation?: () => void
}

function LocationFloatingPanel(props: Props) {
  const intl = useIntl()
  const locationLabels = useLocationSearchLabels()
  const {
    onConfirm = noop,
    onChange = noop,
    searchTypes,
    typeaheadTypes,
    typeaheadVertical,
    placeholder = locationLabels.placeholderLabel,
    isSecondaryLocation,
    title,
    popularPlaces,
    recentSearches,
    isOpen,
    overrideSearchItems,
    updateSearchSessionId,
    multiline = false,
    showNaturalLanguageSearchSuggestions = false,
    label = locationLabels.titleLabel,
    userInteractions,
    setTravelDatesShowAIGlow,
    setOccupancyShowAIGlow,
    temporarySearchSubmitRef,
    onUserSelectedLocation,
  } = props

  const resolvedTitle = title ?? intl.formatMessage({
    id: 'IGGMGzIhjW',
    defaultMessage: 'Where are you going?',
    description: 'Default title for the location search floating panel',
  })

  // When user explicitly selects a location, advance to the next step
  // We call onConfirm() directly here instead of using useMountedEffect on searchItem
  // because searchItem can change for reasons other than user selection (e.g., extraction updating metadata)
  const handleLocationChange = useCallback((item?: App.SearchItem) => {
    if (item) {
      onUserSelectedLocation?.()
      onConfirm()
    }
    onChange(item)
  }, [onChange, onUserSelectedLocation, onConfirm])

  return (
    <SearchFloatingPanel
      {...props}
      title={resolvedTitle}
      isOpen={isOpen}
      isConfirmButtonHidden
    >
      <SearchLocationInput
        variant="inline"
        placeholder={placeholder}
        searchTypes={searchTypes}
        placeTypes={typeaheadTypes}
        searchVertical={typeaheadVertical}
        isSecondaryLocation={isSecondaryLocation}
        recentSearchItems={recentSearches}
        placeholderItems={popularPlaces}
        overrideSearchItems={overrideSearchItems}
        onChange={handleLocationChange}
        updateSearchSessionId={updateSearchSessionId}
        multiline={multiline}
        showNaturalLanguageSearchSuggestions={showNaturalLanguageSearchSuggestions}
        label={label}
        userInteractions={userInteractions}
        setTravelDatesShowAIGlow={setTravelDatesShowAIGlow}
        setOccupancyShowAIGlow={setOccupancyShowAIGlow}
        temporarySearchSubmitRef={temporarySearchSubmitRef}
      />
    </SearchFloatingPanel>
  )
}

export default LocationFloatingPanel
